"use client";

import type { ConstantsNotificationRuleResponse } from "@gosaas/types";
import * as React from "react";
import { toast } from "sonner";
import { useTeam } from "@/components/providers/team";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { apiClient, requestWithHandling } from "@/utils/api";

interface DeleteNotificationRuleDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	rule: ConstantsNotificationRuleResponse | null;
	onSuccess?: () => void;
}

export function DeleteNotificationRuleDialog({
	open,
	onOpenChange,
	rule,
	onSuccess,
}: DeleteNotificationRuleDialogProps) {
	const [isLoading, setIsLoading] = React.useState(false);
	const { teamID } = useTeam();
	const mountedRef = React.useRef(true);
	React.useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	async function onDelete() {
		if (!rule?.id) return;
		try {
			setIsLoading(true);
			const response = await requestWithHandling<void>(() =>
				apiClient.api.teamNotificationsRulesDelete(
					teamID as string,
					rule.id as string,
				),
			);
			if (response.code !== "SUCCESS") {
				toast.error("Failed to delete rule", { description: response.message });
				return;
			}
			toast.success("Rule deleted.");
			onOpenChange(false);
			onSuccess?.();
		} catch {
			toast.error("Couldn't delete this rule", {
				description: "Try again or reload the page.",
			});
		} finally {
			if (mountedRef.current) {
				setIsLoading(false);
			}
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[440px]">
				<DialogHeader>
					<DialogTitle>Delete Rule</DialogTitle>
					<DialogDescription>
						Are you sure you want to delete the rule{" "}
						<strong>{rule?.name}</strong>?
					</DialogDescription>
				</DialogHeader>
				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Cancel
					</Button>
					<Button variant="destructive" onClick={onDelete} disabled={isLoading}>
						{isLoading ? "Deleting..." : "Delete"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
